import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { supabase } from '@/shared/supabase/client';

type CurrencyCode = 'VND' | 'USD';

interface CurrencyContextType {
  currency: CurrencyCode;
  setCurrency: (c: CurrencyCode) => void;
  exchangeRate: number;
  exchangeRateUpdatedAt: string | null;
  exchangeRateUpdatedBy: string | null;
  isLoadingRate: boolean;
  convertFromVnd: (vndValue: number) => number;
  formatMoney: (vndValue: number) => string;
  formatMoneyCompact: (vndValue: number) => string;
  currencyLabel: string;
  refreshRate: () => Promise<void>;
}

const DEFAULT_RATE = 25000;
const STORAGE_KEY = 'medtour_currency_preference';

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

export function CurrencyProvider({ children }: { children: ReactNode }) {
  const [currency, setCurrencyState] = useState<CurrencyCode>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return (stored === 'USD' ? 'USD' : 'VND') as CurrencyCode;
  });
  const [exchangeRate, setExchangeRate] = useState(DEFAULT_RATE);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [updatedBy, setUpdatedBy] = useState<string | null>(null);
  const [isLoadingRate, setIsLoadingRate] = useState(true);

  const fetchRate = useCallback(async () => {
    setIsLoadingRate(true);
    try {
      const { data } = await supabase
        .from('app_settings' as any)
        .select('value, updated_at, updated_by')
        .eq('key', 'exchange_rate_vnd_usd')
        .maybeSingle();
      if (data) {
        const rate = parseFloat((data as any).value);
        if (rate > 0) setExchangeRate(rate);
        setUpdatedAt((data as any).updated_at);
        setUpdatedBy((data as any).updated_by);
      }
    } catch (err) {
      console.error('Failed to fetch exchange rate:', err);
    } finally {
      setIsLoadingRate(false);
    }
  }, []);

  useEffect(() => { fetchRate(); }, [fetchRate]);

  const setCurrency = (c: CurrencyCode) => {
    setCurrencyState(c);
    localStorage.setItem(STORAGE_KEY, c);
  };

  const convertFromVnd = useCallback((vndValue: number): number => {
    if (currency === 'VND') return vndValue;
    return vndValue / exchangeRate;
  }, [currency, exchangeRate]);

  const formatMoney = useCallback((vndValue: number): string => {
    if (currency === 'VND') {
      return `VND ${Math.round(vndValue).toLocaleString('en-US')}`;
    }
    const usd = vndValue / exchangeRate;
    return `USD ${usd.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }, [currency, exchangeRate]);

  const formatMoneyCompact = useCallback((vndValue: number): string => {
    const value = currency === 'VND' ? vndValue : vndValue / exchangeRate;

    if (currency === 'VND') {
      if (value >= 1_000_000_000) return `VND ${(value / 1_000_000_000).toFixed(1)}B`;
      if (value >= 1_000_000) return `VND ${(value / 1_000_000).toFixed(1)}M`;
      if (value >= 1_000) return `VND ${(value / 1_000).toFixed(0)}K`;
      return `VND ${Math.round(value)}`;
    }
    if (value >= 1_000_000) return `USD ${(value / 1_000_000).toFixed(2)}M`;
    if (value >= 1_000) return `USD ${(value / 1_000).toFixed(1)}K`;
    return `USD ${value.toFixed(2)}`;
  }, [currency, exchangeRate]);

  const currencyLabel = currency === 'VND' ? 'VND' : 'USD';

  return (
    <CurrencyContext.Provider value={{
      currency,
      setCurrency,
      exchangeRate,
      exchangeRateUpdatedAt: updatedAt,
      exchangeRateUpdatedBy: updatedBy,
      isLoadingRate,
      convertFromVnd,
      formatMoney,
      formatMoneyCompact,
      currencyLabel,
      refreshRate: fetchRate,
    }}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  const context = useContext(CurrencyContext);
  if (!context) throw new Error('useCurrency must be used within CurrencyProvider');
  return context;
}
